import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BottomData,
  DateDropBox,
  Header,
  HeaderText,
  ItemMainDiv,
  LeftDiv,
  HeaderRightContent,
  RightDiv,
  TopHeader,
  Text,
  Export,
  HeaderTopSection
} from './styles';
import dayjs from 'dayjs';
import { DateRange } from 'react-date-range';
import ReportDurationDropdown from '../../component/dropdowns/reportDurationDropdown';
import {
  CHIP_ICON_POSITION,
  CHIP_SIZE,
  CHIP_TYPE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  PREFERENCE_WRITE_DEBOUNCE_MS,
  TIME_DATE_SELECTED_TEXT
} from '../../global/constants';
import { useHistory } from 'react-router-dom';
import { SelectionRangeInterface, TimeLogInterface } from '../../interfaces/TimeReportInterface';
import { ReportTimeFilterInterface, SelectRangeInterface } from '../../interfaces/ReportInterface';
import { getDayFromDate, getMonthRange, isEmpty } from '../../helpers/common';
import { loadMyTimeLog } from '../../services/timeTrackingServices';
import { ProjectDetailInterface } from '../../interfaces/ProjectInterface';
import { updateUserWorkspacePreference } from '../../helpers/firebaseHelper';
import { Dropdown, DropdownItem } from '../../component/dropdown';
import { rootStore } from '../../mobx/rootStore';
import { useMobileDevice } from '../../global/useMobile';
import { observer } from 'mobx-react-lite';
import IconButton from '../../component/button/iconButton';
import { MyTimeLogCSVHeader } from '../../global/row';
import Chip from '../../component/chipComponent';
import { captureException } from '../../services/logService';
import { CsvLink } from '../../component/tableHeader/styles';
import debounce from 'lodash/debounce';

interface Props {
  loading: boolean;
  setLoading: (value: boolean) => void;
  setTotalTime: (time: string) => void;
  setFilterLogList: (logData: TimeLogInterface[]) => void;
  totalTime: string;
  filterLogList: TimeLogInterface[];
}

const MyTimelogHeader = ({ loading, setLoading, setTotalTime, setFilterLogList, totalTime, filterLogList }: Props) => {
  // States
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [searchProject, setSearchProject] = useState('');
  const [filterProjectList, setFilterProjectList] = useState<ProjectDetailInterface[]>([]);

  //mobx variables
  const { projectStore, workspaceStore, timeTrackingStore } = rootStore;
  const { projects } = projectStore;
  const { currentWorkspace } = workspaceStore;
  const { myTimeLog, myTimeLogFilter, setMyTimeLogFilter } = timeTrackingStore;

  // Other variables
  const history = useHistory();
  const mobile = useMobileDevice();

  // Debounce Firebase preference writes: local state updates immediately for instant UI feedback,
  // while rapid-fire filter toggles (project/date) are coalesced into a single Firestore commit.
  // The workspace id is captured at schedule time and passed explicitly, so a pending/flushed write
  // always lands in the workspace that was active when it was scheduled — never the one switched to.
  const debouncedWritePreference = useRef(
    debounce((data: ReportTimeFilterInterface, workspaceId?: string) => {
      void updateUserWorkspacePreference({ my_timelog_filter: data }, workspaceId);
    }, PREFERENCE_WRITE_DEBOUNCE_MS)
  ).current;

  // Flush any pending preference write on unmount so the last change isn't lost.
  useEffect(() => () => debouncedWritePreference.flush(), [debouncedWritePreference]);

  // redirect to previous page
  const handleBackClick = () => {
    history.goBack();
  };

  // open project dropdown
  const openProjectDropdown = useCallback((value: boolean) => {
    setIsProjectDropdownOpen(value);
    setIsStatusDropdownOpen(false);
    setIsDateDropdownOpen(false);
  }, []);

  // open status dropdown
  const openStatusDropdown = useCallback((value: boolean) => {
    setIsStatusDropdownOpen(value);
    setIsDateDropdownOpen(false);
  }, []);

  // open date dropdown
  const openDateDropdown = useCallback((value: boolean) => {
    setIsDateDropdownOpen(value);
  }, []);

  // convert decimal to hours and minutes
  const convertDecimalToHoursMinutes = (decimalHours: number, isFullString: boolean, withoutInitials?: boolean) => {
    const hours = Math.floor(decimalHours); // Get the integer part (hours)
    const minutes = Math.round((decimalHours - hours) * 60); // Convert decimal to minutes
    return isFullString
      ? `${hours} hours ${minutes} minutes`
      : withoutInitials
      ? `${hours} : ${minutes}`
      : `${hours}h ${minutes}m`;
  };

  // for calculating total time
  const calculateTotalTime = useCallback(
    (list: TimeLogInterface[]) => {
      const total = list?.reduce((acc: number, item: TimeLogInterface) => {
        return acc + (item?.hours || 0);
      }, 0);
      const timeString = convertDecimalToHoursMinutes(total, true);
      setTotalTime(timeString);
    },
    [setTotalTime]
  );

  // Prepares and memoizes CSV data with formatted dates.
  const getExportCsvData = useMemo(() => {
    const csvData = filterLogList?.map((item) => ({
      ...(item || {}),
      date: dayjs(item?.date).format('DD-MM-YYYY')
    }));
    return csvData || [];
  }, [filterLogList]);

  // implement filter logic
  const applyFilter = useCallback(
    async (
      projects: ProjectDetailInterface[],
      selectionRange: SelectRangeInterface,
      myTimeReportData?: TimeLogInterface[],
      projectList?: ProjectDetailInterface[]
    ) => {
      const selectedProjects = projects?.filter((x) => x?.checked);
      if (!projectList) projectList = projects;
      const selectedProjectIds = selectedProjects?.map((x) => x?.id);
      if (!myTimeReportData) myTimeReportData = myTimeLog;
      let timeLogData: TimeLogInterface[] = JSON.parse(JSON.stringify(myTimeReportData));
      const startDate = dayjs(selectionRange.startDate);
      const endDate = dayjs(selectionRange.endDate).add(1, 'day');
      timeLogData = timeLogData?.filter(
        (item) =>
          selectedProjectIds?.includes(item?.projectId) &&
          dayjs(item?.date) >= startDate &&
          dayjs(item?.date) <= endDate
      );
      const formattedTimeLogData = timeLogData?.map((item) => ({
        ...(item || {}),
        day: getDayFromDate(item?.date)
      }));
      setFilterLogList(formattedTimeLogData || []);
      calculateTotalTime(timeLogData || []);
    },
    [calculateTotalTime, myTimeLog, setFilterLogList]
  );

  // update date range
  const updateDateRangeChange = useCallback(
    async (startDate: Date, endDate: Date, selected_text: string) => {
      try {
        const selectDates: SelectionRangeInterface = {
          startDate: startDate,
          endDate: endDate,
          key: 'selection'
        };
        setIsStatusDropdownOpen(false);
        const data: ReportTimeFilterInterface = {
          ...(myTimeLogFilter || {}),
          selected_range: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            key: 'selection'
          },
          selected_text: selected_text
        };
        setMyTimeLogFilter(data);
        if (myTimeLogFilter?.project_list && currentWorkspace?.id) {
          const monthRange = getMonthRange(startDate.toISOString(), endDate.toISOString());
          const loadedLogs = await loadMyTimeLog(currentWorkspace?.id, monthRange);
          applyFilter(myTimeLogFilter?.project_list, selectDates, loadedLogs as unknown as TimeLogInterface[]);
        }
        // Update my timelog filter in workspace preference
        debouncedWritePreference(data, currentWorkspace?.id);
      } catch (e) {
        captureException(e);
      }
    },
    [myTimeLogFilter, setMyTimeLogFilter, currentWorkspace?.id, applyFilter, debouncedWritePreference]
  );

  // handle select date range
  const handleSelectDateRange = useCallback(
    async (ranges: { selection: { startDate: Date; endDate: Date; key: string } }) => {
      try {
        setLoading(true);
        const data = {
          ...(myTimeLogFilter || {}),
          selected_range: {
            startDate: new Date(ranges.selection.startDate).toISOString(),
            endDate: new Date(ranges.selection.endDate).toISOString(),
            key: ranges.selection.key
          },
          selected_text: TIME_DATE_SELECTED_TEXT.CUSTOM
        };
        setMyTimeLogFilter(data);
        if (myTimeLogFilter?.project_list && currentWorkspace?.id) {
          const monthRange = getMonthRange(data?.selected_range?.startDate, data?.selected_range?.endDate);
          const loadedLogs = await loadMyTimeLog(currentWorkspace?.id, monthRange);
          applyFilter(myTimeLogFilter?.project_list, ranges.selection, loadedLogs as unknown as TimeLogInterface[]);
        }
        // Update my timelog filter in workspace preference
        debouncedWritePreference(data, currentWorkspace?.id);
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, myTimeLogFilter, setMyTimeLogFilter, currentWorkspace?.id, applyFilter, debouncedWritePreference]
  );

  // get project info based on search
  const onChangeSearchProject = useCallback(
    async (value: string) => {
      setSearchProject(value);
      let timeout;
      if (!isEmpty(value)) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          const newList =
            myTimeLogFilter?.project_list?.filter((item: { id: string }) =>
              projects
                ?.find((project) => project?.id === item?.id)
                ?.name?.toLowerCase()
                .includes(value?.toLowerCase())
            ) || [];
          setFilterProjectList(newList);
        }, 300);
      } else {
        if (myTimeLogFilter?.project_list) setFilterProjectList(myTimeLogFilter?.project_list);
      }
    },
    [projects, myTimeLogFilter]
  );

  // get project info based on selected
  const onSelectAllProjectClick = useCallback(
    async (projectList: ProjectDetailInterface[], value: boolean) => {
      let projectListClone: ProjectDetailInterface[] = JSON.parse(JSON.stringify(projectList));
      projectListClone = projectListClone?.map((item) => {
        return { ...item, checked: value };
      });
      const data = {
        ...(myTimeLogFilter || {}),
        project_list: projectListClone,
        selected_all_project: value
      };
      setFilterProjectList(projectListClone);
      setIsProjectDropdownOpen(false);
      if (data) {
        setMyTimeLogFilter(data);
      }
      if (myTimeLogFilter?.selected_range) applyFilter(projectListClone, myTimeLogFilter?.selected_range);
      // Update my timelog filter in workspace preference
      debouncedWritePreference(data, currentWorkspace?.id);
    },
    [applyFilter, myTimeLogFilter, setMyTimeLogFilter, currentWorkspace?.id, debouncedWritePreference]
  );

  // get project info based in selected(check box)
  const onCheckProject = useCallback(
    async (
      allProjectList: ProjectDetailInterface[],
      filteredList: ProjectDetailInterface[],
      projectId: string,
      value: boolean
    ) => {
      const updatedAllList = allProjectList?.map((item) => {
        return item?.id === projectId ? { ...item, checked: value } : item;
      }, []);

      const updatedFilteredList = filteredList?.map((item) => {
        return item?.id === projectId ? { ...item, checked: value } : item;
      }, []);

      const uncheckedTasks = updatedAllList?.find((x) => !x?.checked);
      let newValue;
      if (uncheckedTasks || updatedFilteredList?.length === 0) {
        newValue = false;
      } else {
        newValue = true;
      }
      setIsProjectDropdownOpen(false);
      const data = {
        ...(myTimeLogFilter || {}),
        project_list: updatedAllList,
        selected_all_project: newValue
      };
      setFilterProjectList(updatedFilteredList);
      setMyTimeLogFilter(data);
      if (myTimeLogFilter?.selected_range) applyFilter(updatedAllList, myTimeLogFilter?.selected_range);
      // Update time report filter in workspace preference
      debouncedWritePreference(data, currentWorkspace?.id);
    },
    [applyFilter, myTimeLogFilter, setMyTimeLogFilter, currentWorkspace?.id, debouncedWritePreference]
  );

  // render project data
  const renderProjectData = useMemo(() => {
    return (
      // eslint-disable-next-line react/jsx-filename-extension
      <>
        <BottomData isLoading={loading}>
          {isEmpty(searchProject) && (
            <DropdownItem
              label='Select All'
              onClick={() =>
                onSelectAllProjectClick(myTimeLogFilter?.project_list || [], !myTimeLogFilter?.selected_all_project)
              }
              isCheckBox={true}
              isChecked={myTimeLogFilter?.selected_all_project}
            />
          )}
          {(isEmpty(searchProject) ? myTimeLogFilter?.project_list : filterProjectList)?.map(
            (item: ProjectDetailInterface) => {
              const project = projects?.find((project) => project?.id === item?.id);
              if (!project) return null;
              return (
                <DropdownItem
                  key={item?.id}
                  label={project?.name}
                  isCheckBox={true}
                  isChecked={item?.checked}
                  onClick={(e) =>
                    onCheckProject(myTimeLogFilter?.project_list || [], filterProjectList, item?.id, !item?.checked)
                  }
                />
              );
            }
          )}
        </BottomData>
      </>
    );
  }, [
    filterProjectList,
    projects,
    loading,
    myTimeLogFilter?.project_list,
    myTimeLogFilter?.selected_all_project,
    onCheckProject,
    onSelectAllProjectClick,
    searchProject
  ]);

  return (
    <Header updateSpacing={!loading && filterLogList?.length > 0}>
      <HeaderTopSection>
        {!mobile && (
          <LeftDiv>
            <IconButton
              iconName='new-back-arrow-icon'
              size={ICON_BUTTON_SIZE.SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={handleBackClick}
            />
            <HeaderText>Your Timelog</HeaderText>
          </LeftDiv>
        )}
        <RightDiv>
          <Dropdown
            modalTitle='Project'
            isMinWidth={120}
            onChangeSearch={onChangeSearchProject}
            dropdownContentWidth={mobile ? '100%' : ''}
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                title='All Projects'
                onClick={() => openProjectDropdown(!isProjectDropdownOpen)}
                trailingIconName='down-arrow-icon'
                trailingIconClassName={isProjectDropdownOpen ? 'dropdown-icon' : ''}
                isActive={isProjectDropdownOpen}
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
              />
            }
            trigger='click'
            onOutsideClick={() => openProjectDropdown(!isProjectDropdownOpen)}>
            <ItemMainDiv>{renderProjectData}</ItemMainDiv>
          </Dropdown>
          <HeaderRightContent>
            <Dropdown
              modalTitle='Custom Date'
              isMinWidth={152}
              content={
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                  title={
                    myTimeLogFilter?.selected_text ? myTimeLogFilter?.selected_text : TIME_DATE_SELECTED_TEXT.TODAY
                  }
                  onClick={() => openStatusDropdown(!isStatusDropdownOpen)}
                  className='custom-dropdown'
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={isStatusDropdownOpen ? 'dropdown-icon' : ''}
                  isActive={isStatusDropdownOpen}
                />
              }
              trigger='click'
              onOutsideClick={() => openStatusDropdown(!isStatusDropdownOpen)}>
              <ReportDurationDropdown
                setIsStatusDropdownOpen={(value) => setIsStatusDropdownOpen(value as boolean)}
                updateDateRangeChange={(startDate: Date, endDate: Date, selected_text: string) =>
                  updateDateRangeChange(startDate, endDate, selected_text)
                }
              />
            </Dropdown>
            <div>
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title={`${dayjs(myTimeLogFilter?.selected_range?.startDate).format('MMM DD')} - ${dayjs(
                  myTimeLogFilter?.selected_range?.endDate
                ).format('MMM DD')}`}
                leadingIconName='calendar-icon'
                trailingIconName='down-arrow-icon'
                className='calendar-dropdown'
                trailingIconClassName={isDateDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => openDateDropdown(!isDateDropdownOpen)}
                isActive={isDateDropdownOpen}
              />
              {isDateDropdownOpen && (
                <DateDropBox>
                  <DateRange
                    editableDateInputs={true}
                    onChange={handleSelectDateRange}
                    ranges={[
                      {
                        startDate: new Date(myTimeLogFilter?.selected_range?.startDate || new Date()),
                        endDate: new Date(myTimeLogFilter?.selected_range?.endDate || new Date()),
                        key: myTimeLogFilter?.selected_range?.key
                      }
                    ]}
                    moveRangeOnFirstSelection={false}
                    maxDate={new Date()}
                  />
                </DateDropBox>
              )}
            </div>
          </HeaderRightContent>
        </RightDiv>
      </HeaderTopSection>
      {!loading && filterLogList?.length > 0 && (
        <TopHeader>
          <Text>Total time logged: {totalTime}</Text>
          <CsvLink data={getExportCsvData} headers={MyTimeLogCSVHeader} filename={`time_log_report.csv`}>
            <Export>Export</Export>
          </CsvLink>
        </TopHeader>
      )}
    </Header>
  );
};

export default observer(MyTimelogHeader);
